import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import {
  compareVersions,
  getCurrentAppVersion,
  useAppVersion,
} from '@/app/services/use-app-version';
import { useAuthStore } from '@/auth/auth-store';
import { useState } from 'react';

/**
 * Upgrade alert wrapper.
 *
 * Mirrors Flutter `menu_container_screen.dart:407` (`UpgradeAlert(child:
 * ...)`) — wraps the app root and renders either:
 *
 *   - A blocking AlertDialog when `currentVersion < minSupportVersion`
 *     (Flutter `forceUpdateRequired === true`) — user can't proceed
 *     without reloading
 *   - A dismissable banner when `currentVersion < latestVersion` but
 *     >= minSupportVersion — soft nudge to refresh
 *   - Nothing when current >= latest
 *
 * Runs on authenticated session only; pre-login pages skip the version
 * check (no point asking the version service for an unauthenticated
 * shell — the api-client would 401 anyway).
 */

export function UpgradeAlert() {
  const user = useAuthStore((s) => s.user);
  const [dismissed, setDismissed] = useState(false);
  const versionQuery = useAppVersion();

  // Gate on auth: pre-login the API call would 401, and there's no
  // surface to upgrade from yet anyway.
  if (!user) return null;
  if (!versionQuery.data) return null;

  const current = getCurrentAppVersion();

  // Local dev builds have no baked VITE_APP_VERSION, so getCurrentAppVersion()
  // returns the '0.0.0' sentinel. That's not a real released version — comparing
  // it against the backend's semver LatestVersion always reports "stale", so the
  // soft banner (and, when the backend sends a minSupportVersion, the *blocking*
  // "Update required" dialog) would fire on every load and never clear on reload
  // (a reload can't change a version inlined into the bundle). Skip the check for
  // the sentinel. Deployed builds bake a real value in CI (dev-web.yml
  // VITE_APP_VERSION) and are unaffected.
  if (current === '0.0.0') return null;
  const { latestVersion, minSupportVersion, forceUpdateRequired, message } =
    versionQuery.data;

  if (!latestVersion) return null;

  const isForced =
    forceUpdateRequired ||
    (minSupportVersion && compareVersions(current, minSupportVersion) < 0);
  const isStale = compareVersions(current, latestVersion) < 0;

  if (isForced) {
    return (
      <AlertDialog open>
        <AlertDialogContent className="sm:max-w-[var(--dialog-width-confirm)]">
          <AlertDialogHeader>
            <AlertDialogTitle>Update required</AlertDialogTitle>
            <AlertDialogDescription className="[font:var(--type-body)] text-[var(--ink-secondary)]">
              {message ||
                `A required update is available (${latestVersion}). Reload the page to continue.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => window.location.reload()}>
              Reload now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  if (isStale && !dismissed) {
    return (
      <div className="fixed top-3 left-1/2 -translate-x-1/2 z-50 rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] px-4 py-3 shadow-[var(--shadow-4)] flex items-center gap-3 [font:var(--type-body)]">
        <span className="text-[var(--ink-body)]">
          A new version ({latestVersion}) is available.
        </span>
        <Button
          type="button"
          size="sm"
          onClick={() => window.location.reload()}
        >
          Refresh
        </Button>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => setDismissed(true)}
          className="text-[var(--ink-secondary)] hover:text-[var(--ink-body)]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return null;
}
